import { useEffect, useMemo, useState } from 'react';
import type { DescuentoDeVenta, LineaMostrador } from '@tanteo/compartido';
import { calcularTotales, formatearCOP, llevarATotal } from '@tanteo/compartido';
import type { DescuentoDelNegocio } from '../../lib/api-descuentos';
import { DIGITOS_DE_PLATA } from '../../lib/lector';
import { Boton, CampoNumero, FilaAcciones, GrupoOpciones } from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { textoDeDescuento } from './textoDeDescuento';

type ModoLibre = DescuentoDeVenta['tipo'] | 'dejar';

/** El descuento a la venta completa, entre los que configuró el dueño. Arranca mostrando el total actual. */
export function DescuentoVenta({
  abierto,
  lineas,
  descuento,
  descuentos,
  onAplicar,
  onCerrar,
}: {
  abierto: boolean;
  lineas: LineaMostrador[];
  descuento: DescuentoDeVenta | null;
  /** Los descuentos a la venta que quien vende puede dar. */
  descuentos: DescuentoDelNegocio[];
  onAplicar: (descuento: DescuentoDeVenta | null) => void;
  onCerrar: () => void;
}) {
  const [elegido, setElegido] = useState<string | null>(null);
  const [modoLibre, setModoLibre] = useState<ModoLibre>('pct');
  const [valorLibre, setValorLibre] = useState<number | null>(null);

  useEffect(() => {
    if (!abierto) return;
    setElegido(descuento?.descuentoId ?? null);
    setModoLibre(descuento?.libre ? descuento.tipo : 'pct');
    setValorLibre(descuento?.libre ? descuento.valor : null);
  }, [abierto, descuento]);

  const escogido = descuentos.find((d) => d.id === elegido) ?? null;
  const candidato = useMemo((): DescuentoDeVenta | null => {
    if (!escogido) return null;
    const base = { descuentoId: escogido.id, nombre: escogido.nombre, libre: escogido.forma === 'libre' };
    if (escogido.forma === 'porcentaje') return { ...base, tipo: 'pct', valor: escogido.valor ?? 0 };
    if (escogido.forma === 'monto_fijo') return { ...base, tipo: 'monto', valor: escogido.valor ?? 0 };
    if (valorLibre === null || valorLibre <= 0) return null;
    if (modoLibre === 'dejar') {
      const monto = llevarATotal(lineas, valorLibre);
      return monto ? { ...base, tipo: 'monto', valor: monto } : null;
    }
    return { ...base, tipo: modoLibre, valor: valorLibre };
  }, [escogido, modoLibre, valorLibre, lineas]);

  if (!abierto) return null;
  const antes = calcularTotales(lineas);
  const despues = calcularTotales(lineas, candidato);
  const listo = candidato !== null && despues.total < antes.total;

  return (
    <Modal abierto titulo="Descuento a la venta" onCerrar={onCerrar}>
      <div className="space-y-4" data-e2e="descuento-venta">
        <GrupoOpciones
          etiqueta="Descuento"
          valor={elegido}
          onElegir={setElegido}
          opciones={descuentos.map((d) => ({
            valor: d.id,
            texto: textoDeDescuento(d, 'venta'),
            e2e: `descuento-opcion-${d.nombre}`,
          }))}
        />
        {escogido?.forma === 'libre' && (
          <div className="space-y-3">
            <GrupoOpciones
              etiqueta="Tipo de descuento libre"
              valor={modoLibre}
              onElegir={(m) => {
                setModoLibre(m);
                setValorLibre(null);
              }}
              opciones={[
                { valor: 'pct', texto: 'Porcentaje' },
                { valor: 'monto', texto: 'Rebaja $' },
                { valor: 'dejar', texto: 'Dejar en $' },
              ]}
              e2e={(o) => `descuento-modo-${o.valor}`}
            />
            <CampoNumero
              etiqueta={modoLibre === 'pct' ? 'Porcentaje' : modoLibre === 'dejar' ? 'Total a cobrar' : 'Pesos menos'}
              plata={modoLibre !== 'pct'}
              min={0}
              max={modoLibre === 'pct' ? 100 : undefined}
              valor={valorLibre}
              onValor={setValorLibre}
              lector={{ maxDigitos: DIGITOS_DE_PLATA }}
              data-e2e="descuento-valor"
            />
          </div>
        )}

        <div className="bg-surface rounded-card num grid grid-cols-3 gap-3 p-4">
          <div>
            <span className="text-muted block text-contenido">Subtotal</span>
            <span className="text-titulo font-bold">{formatearCOP(antes.total)}</span>
          </div>
          <div>
            <span className="text-muted block text-contenido">Descuento</span>
            <span className="text-destructive text-titulo font-bold">−{formatearCOP(antes.total - despues.total)}</span>
          </div>
          <div>
            <span className="text-muted block text-contenido">Total</span>
            <span className="text-titulo font-extrabold" data-e2e="descuento-queda">
              {formatearCOP(despues.total)}
            </span>
          </div>
        </div>
        {escogido?.forma === 'libre' && modoLibre === 'dejar' && valorLibre !== null && candidato && despues.total < valorLibre && (
          <p className="text-muted text-contenido">Con impuestos no cae exacto: queda en {formatearCOP(despues.total)}.</p>
        )}

        <FilaAcciones>
          {descuento && (
            <Boton variante="contorno" onClick={() => onAplicar(null)} data-e2e="descuento-quitar">
              Quitar descuento
            </Boton>
          )}
          <Boton variante="contorno" onClick={onCerrar}>
            Volver
          </Boton>
          <Boton disabled={!listo} onClick={() => candidato && onAplicar(candidato)} data-e2e="descuento-aplicar">
            Aplicar
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
